import type { Corpus } from "@/pages/dev/smart-cms/shared/corpus/db"

/**
 * The corpus section appended to every agent's system prompt.
 *
 * Kept separate from the four backend prompt builders so the guidance is
 * written once: the navigation strategy is identical no matter which CMS the
 * agent is driving.
 *
 * Returns "" when nothing is attached, so an agent with no documents is not
 * told about tools that would return nothing.
 */
export function buildCorpusPromptSection(corpora: Corpus[]): string {
  if (!corpora.length) return ""

  const list = corpora
    .map(c => `  - "${c.name}" (id: ${c.id}) — ${c.fileCount} files`)
    .join("\n")

  return `

UPLOADED DOCUMENTS:
The user has attached reference material to this conversation:
${list}

These are documents the user chose to give you — treat them as authoritative
context for this work, ahead of your own assumptions about how things are
usually done.

HOW TO USE THEM — navigate, do not ingest:
- Do NOT try to read everything. These tools work like a code editor's: you
  search first, then read only what matched.
- corpus_tree shows the folder structure. Skim it first — file and folder
  NAMES tell you where a topic lives.
- corpus_grep searches the full text and returns path + line number for each
  hit. This is your main entry point. Search a distinctive phrase, not a whole
  question: "unaccompanied minor" finds more than "what is our policy on
  unaccompanied minors".
- corpus_read reads a slice around a line. Pass a grep hit's line number as
  offset. Read more only if the slice turns out to be insufficient.
- corpus_outline lists a document's headings with line numbers — the cheapest
  way to navigate a long document.

RULES:
- Search the documents BEFORE answering a question they plausibly cover, and
  before creating content in a domain they describe. Do not guess at something
  the user has handed you the answer to.
- CITE what you use: name the file and, where it helps, the line or page. The
  user needs to be able to check you.
- If the documents do not cover something, say so plainly rather than filling
  the gap with a confident guess. "The uploaded policy does not address X" is
  a useful answer.
- If they contradict what you were about to do, follow the documents and flag
  the discrepancy.`
}
